import React, { useMemo, useCallback } from "react";

import { interpolateHcl } from "d3-interpolate";

import { ThresholdLine } from "../../components/ThresholdLine";

import type { ThresholdType, XYChartThresholdsProps } from "../types";

const colorInterpolation = interpolateHcl("#D83B3E", "#FF8F27");

const XYChartThresholds = ({
  thresholds,
  leftScale,
  rightScale,
  bottomScale,
  leftAxisWidth,
  xMax,
  yMax,
  parentWidth,
}: XYChartThresholdsProps) => {
  const getThresholdPosition = useCallback(
    (axis: "left" | "right" | "bottom", value: number) => {
      switch (axis) {
        case "left":
          return leftScale(value);
        case "right":
          return rightScale(value);
        case "bottom":
          return bottomScale(value) + leftAxisWidth;
      }
    },
    [leftScale, rightScale, bottomScale]
  );

  const displayThresholds = useMemo(
    () =>
      thresholds.reduce(
        (
          total: Array<ThresholdType & { thresholdPosition: number }>,
          { axis, value, ...restThreshold }
        ) => {
          const thresholdPosition = getThresholdPosition(axis, value);

          const largerIndex = total.findIndex(
            ({ thresholdPosition: currentThresholdPosition }) =>
              currentThresholdPosition > thresholdPosition
          );

          const insertionIndex =
            largerIndex === -1 ? total.length : largerIndex;

          total.splice(insertionIndex, 0, {
            thresholdPosition,
            value,
            axis,
            ...restThreshold,
          });

          return total;
        },
        []
      ),
    [thresholds, parentWidth, getThresholdPosition]
  );

  return (
    <>
      {displayThresholds?.map(({ thresholdPosition, axis }, index) => (
        <ThresholdLine
          key={`axis-threshold-${index}`}
          left={leftAxisWidth}
          value={thresholdPosition}
          xMax={xMax}
          yMax={yMax}
          axis={axis}
          colour={colorInterpolation(
            axis === "bottom" ? 0 : index / (displayThresholds.length - 1)
          )}
        />
      ))}
    </>
  );
};

export default XYChartThresholds;
